import React, { useState } from 'react';
import axios from 'axios';
import {
  FaChartLine, FaBell, FaEye, FaUnlock, FaLock, FaPlus, FaTrash, FaStar, FaHistory,
  FaEdit, FaClipboardCheck,
} from 'react-icons/fa';
import { formatDateForDisplay, formatDateForInput, renderReminderTracking, formSelectClass, formInputClass } from './menuTabUtils';

const formLabelClass = 'block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5';
const formCardClass = 'p-4 rounded-xl bg-gray-50 dark:bg-neutral-800/50 border border-gray-100 dark:border-neutral-700';

const DEFAULT_TAX_SUMMARY_TABLE = {
  columns: ['PARTICULARS', 'CURRENT YEAR', 'PREVIOUS YEAR'],
  rows: [
    ['Total Income  ', '', ''],
    ['Total Tax ', '', ''],
  ],
};

const DEFAULT_TAX_FILING_STEPS = [
  { step: 1, title: 'Initial Information Required', status: 'in-progress' },
  { step: 2, title: 'Tax Return Preparation', status: 'pending' },
  { step: 3, title: 'Draft Tax Return', status: 'pending' },
  { step: 4, title: 'Tax Filing', status: 'pending' },
];

const getDueDatePeriod = (formType) => {
  if (formType === '1040' || formType === '1120') return '3.5 months';
  if (formType === '1065' || formType === '1120-S') return '2.5 months';
  return '';
};

const formatTaxFilingStatusLabel = (status) => {
  if (status === 'in-progress') return 'IN PROGRESS';
  if (status === 'client-pending') return 'CLIENT PENDING';
  if (status === 'submitted') return 'COMPLETED';
  return 'PENDING';
};

const areAllStepsCompletedForUser = (user, memberIndex = null) => {
  let steps;
  if (memberIndex !== null && memberIndex >= 0) {
    steps = user?.members?.[memberIndex]?.steps;
  } else {
    steps = user?.steps;
  }
  if (!steps || steps.length === 0) return false;
  const relevantSteps = steps.filter((s) => s.step <= 4);
  return relevantSteps.length >= 4 && relevantSteps.every((s) => s.status === 'submitted');
};

const TaxFilingTab = ({
  selectedMenuUser,
  setSelectedMenuUser,
  userSteps,
  refreshUserSteps,
  handleFormAssignment,
  handleMemberFormAssignment,
  handleMemberNameUpdate,
  addMember,
  removeMember,
  handleTaxDateUpdate,
  handleMemberTaxDateUpdate,
  handlePaymentStatusUpdate,
  handleMoveToHistory,
  handleUnlockTaxFiling,
  handleUpdateTaxSummaryTable,
  handleHistoryTaxDateUpdate,
  handleOpenStep1Checklist,
  onPrepareChecklist,
  getStep3Status,
  updateClientFromUser,
  initialMemberIndex,
}) => {
  const [taxFilingSubTab, setTaxFilingSubTab] = useState('primary-member');
  const [activeMemberIndex, setActiveMemberIndex] = useState(0);

  React.useEffect(() => {
    if (initialMemberIndex !== undefined && initialMemberIndex !== null && initialMemberIndex >= 0) {
      setActiveMemberIndex(initialMemberIndex);
      setTaxFilingSubTab('member');
    } else if (initialMemberIndex === -1) {
      setTaxFilingSubTab('primary-member');
    }
  }, [initialMemberIndex, selectedMenuUser]);
  const [primaryTaxYearEndDate, setPrimaryTaxYearEndDate] = useState('');
  const [primaryDueDate, setPrimaryDueDate] = useState('');
  const [memberTaxYearEndDates, setMemberTaxYearEndDates] = useState({});
  const [memberDueDates, setMemberDueDates] = useState({});
  const [newMemberName, setNewMemberName] = useState('');
  const [updatingTaxFilingStep, setUpdatingTaxFilingStep] = useState({});
  const [manualReminderLoading, setManualReminderLoading] = useState(false);
  const [newCycleTaxYearEndDate, setNewCycleTaxYearEndDate] = useState('');
  const [newCycleDueDate, setNewCycleDueDate] = useState('');
  const [editingHistory, setEditingHistory] = useState(null);
  const [historyEditTaxYearEnd, setHistoryEditTaxYearEnd] = useState('');
  const [historyEditDueDate, setHistoryEditDueDate] = useState('');

  const cloneDefaultTable = () => ({
    columns: [...DEFAULT_TAX_SUMMARY_TABLE.columns],
    rows: DEFAULT_TAX_SUMMARY_TABLE.rows.map((row) => [...row]),
  });

  const getPrimarySummaryTable = () => {
    const t = selectedMenuUser?.taxFilingSummaryTable;
    if (t && Array.isArray(t.columns) && t.columns.length > 0) return t;
    return cloneDefaultTable();
  };

  const handlePrimaryTableChange = (updater) => {
    setSelectedMenuUser((prev) => {
      if (!prev) return prev;
      const t = prev.taxFilingSummaryTable;
      const current = (t && Array.isArray(t.columns) && t.columns.length > 0) ? t : cloneDefaultTable();
      return { ...prev, taxFilingSummaryTable: updater(current) };
    });
  };

  const getMemberSummaryTable = (member) => {
    const t = member?.taxFilingSummaryTable;
    if (t && Array.isArray(t.columns) && t.columns.length > 0) return t;
    return cloneDefaultTable();
  };

  const handleMemberTableChange = (memberIndex, updater) => {
    setSelectedMenuUser((prev) => {
      if (!prev?.members) return prev;
      const members = [...prev.members];
      const currentMember = members[memberIndex];
      if (!currentMember) return prev;
      const t = currentMember.taxFilingSummaryTable;
      const currentTable = (t && Array.isArray(t.columns) && t.columns.length > 0) ? t : DEFAULT_TAX_SUMMARY_TABLE;
      members[memberIndex] = { ...currentMember, taxFilingSummaryTable: updater(currentTable) };
      return { ...prev, members };
    });
  };

  const formatDetailedDateTime = (dateString) => {
    if (!dateString) return <span className="text-gray-400 italic">Not yet recorded</span>;
    const date = new Date(dateString);
    return (
      <div className="flex flex-col">
        <span className="font-semibold text-gray-900 dark:text-white">
          {date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
        </span>
      </div>
    );
  };

  const formatTaxFilingStatusShort = (status) => {
    if (status === 'in-progress') return 'In Progress';
    if (status === 'client-pending') return 'Client Pending';
    if (status === 'submitted') return 'Completed';
    return 'Pending';
  };

  const getTaxFilingStepsList = (memberIndex = -1) => {
    const raw = memberIndex >= 0
      ? userSteps[selectedMenuUser?._id]?.memberSteps?.[memberIndex]
      : userSteps[selectedMenuUser?._id]?.steps;
    if (!raw?.length) return DEFAULT_TAX_FILING_STEPS;
    return DEFAULT_TAX_FILING_STEPS.map((ds) => {
      const existing = raw.find((s) => s.step === ds.step);
      return existing ? { ...ds, ...existing, title: existing.title || ds.title } : ds;
    });
  };

  const handleUpdateTaxFilingStepStatus = async (stepNumber, status, memberIndex = -1) => {
    const key = `${memberIndex}-${stepNumber}`;
    setUpdatingTaxFilingStep((prev) => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/update-tax-filing-step-status`,
        {
          userId: selectedMenuUser._id,
          stepNumber,
          status,
          ...(memberIndex >= 0 ? { memberIndex } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.user) {
        setSelectedMenuUser(response.data.user);
        updateClientFromUser?.(response.data.user);
      }
      if (refreshUserSteps) await refreshUserSteps(selectedMenuUser._id);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update tax filing step status.');
    } finally {
      setUpdatingTaxFilingStep((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSendTaxReminder = async (userId, memberIndex = null) => {
    if (!window.confirm('Send a tax filing reminder email to this client now?')) return;
    setManualReminderLoading(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-immediate-tax-reminder`,
        { userId, memberIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Reminder email sent successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send reminder email.');
    } finally {
      setManualReminderLoading(false);
    }
  };

  const handleAddMemberWithService = async () => {
    try {
      const memberIndex = await addMember(selectedMenuUser._id);
      if (newMemberName) {
        await handleMemberNameUpdate(selectedMenuUser._id, memberIndex, newMemberName);
        setNewMemberName('');
      }
      setActiveMemberIndex(memberIndex);
      setTaxFilingSubTab('member');
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  const renderFeedback = (feedback) => (
    feedback?.rating ? (
      <div className="p-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <FaStar key={star} size={12} className={star <= feedback.rating ? 'text-amber-400' : 'text-gray-200'} />
          ))}
          <span className="text-sm font-bold">{feedback.rating}/5</span>
        </div>
        {feedback.comment && (
          <p className="text-xs italic text-gray-600 dark:text-gray-400">&ldquo;{feedback.comment}&rdquo;</p>
        )}
      </div>
    ) : (
      <p className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl">No feedback submitted yet.</p>
    )
  );

  const renderLockStatus = (locked, memberIndex = null) => (
    <div className={`p-4 rounded-xl ${locked ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {locked ? (
            <><FaLock className="text-orange-500" /><span className="font-medium text-orange-700 dark:text-orange-400 text-sm">Tax Filing is Locked</span></>
          ) : (
            <><FaUnlock className="text-green-500" /><span className="font-medium text-green-700 dark:text-green-400 text-sm">Tax Filing is Unlocked</span></>
          )}
        </div>
        {locked && handleUnlockTaxFiling && (
          <button
            type="button"
            onClick={() => handleUnlockTaxFiling(selectedMenuUser._id, memberIndex)}
            className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 text-sm"
          >
            Unlock
          </button>
        )}
      </div>
    </div>
  );

  const renderMoveToHistory = (memberIndex = null) => {
    const user = memberIndex === null ? selectedMenuUser : selectedMenuUser?.members?.[memberIndex];
    if (!areAllStepsCompletedForUser(selectedMenuUser, memberIndex) || user?.taxFilingLocked) return null;
    return (
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
        <h5 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2 text-sm">
          <FaHistory className="text-primary-500" /> Move to History & Start New Cycle
        </h5>
        <p className="text-sm text-purple-700 dark:text-purple-400 mb-3">All 4 steps completed. Move this cycle to history and start a new locked cycle.</p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <input type="date" value={newCycleTaxYearEndDate} onChange={(e) => setNewCycleTaxYearEndDate(e.target.value)} className={formInputClass} placeholder="Tax Year End" />
          <input type="date" value={newCycleDueDate} onChange={(e) => setNewCycleDueDate(e.target.value)} className={formInputClass} placeholder="Due Date" />
        </div>
        <button
          type="button"
          onClick={() => {
            if (newCycleTaxYearEndDate) {
              handleMoveToHistory(selectedMenuUser._id, memberIndex, newCycleTaxYearEndDate, newCycleDueDate);
              setNewCycleTaxYearEndDate('');
              setNewCycleDueDate('');
            }
          }}
          disabled={!newCycleTaxYearEndDate}
          className="w-full px-4 py-2.5 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <FaHistory /> Move to History & Lock New Cycle
        </button>
      </div>
    );
  };

  const renderTaxFilingStepsStatus = (memberIndex = -1) => {
    const stepsList = getTaxFilingStepsList(memberIndex);
    const hasForm = memberIndex >= 0
      ? selectedMenuUser?.members?.[memberIndex]?.assignedForm
      : selectedMenuUser?.assignedForm;

    return (
      <div className="pt-6 border-t border-gray-100 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaChartLine className="text-primary-500" /> Tax Filing Steps Status
          {memberIndex >= 0 && (
            <span className="text-xs font-normal text-gray-500">
              — {selectedMenuUser?.members?.[memberIndex]?.name || `Member ${memberIndex + 1}`}
            </span>
          )}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stepsList.map((step) => {
            const updateKey = `${memberIndex}-${step.step}`;
            const isUpdating = updatingTaxFilingStep[updateKey];
            const step3Status = getStep3Status?.(selectedMenuUser._id, memberIndex);

            return (
              <div
                key={step.step}
                className={`p-4 rounded-xl border-2 ${
                  step.status === 'submitted' ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                    : step.status === 'in-progress' ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                      : step.status === 'client-pending' ? 'bg-purple-50/50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-800'
                        : 'bg-gray-50/50 dark:bg-neutral-900/50 border-gray-200 dark:border-neutral-700'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Step {step.step}</span>
                  <span className={`px-2 py-0.5 text-[9px] font-black rounded-full uppercase ${
                    step.status === 'submitted' ? 'bg-blue-500 text-white'
                      : step.status === 'in-progress' ? 'bg-amber-500 text-white'
                        : step.status === 'client-pending' ? 'bg-purple-500 text-white'
                          : 'bg-gray-500 text-white'
                  }`}>
                    {formatTaxFilingStatusLabel(step.status)}
                  </span>
                </div>
                <h6 className="font-bold text-gray-900 dark:text-white text-xs mb-3">{step.title}</h6>
                <select
                  value={step.status || 'pending'}
                  onChange={(e) => handleUpdateTaxFilingStepStatus(step.step, e.target.value, memberIndex)}
                  disabled={isUpdating}
                  className="w-full pl-3 pr-8 py-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-xs font-semibold appearance-none"
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  {step.step >= 3 && <option value="client-pending">Client Action Pending</option>}
                  <option value="submitted">Submitted / Completed</option>
                </select>
                {step.step === 1 && hasForm && handleOpenStep1Checklist && (
                  <button
                    type="button"
                    onClick={() => handleOpenStep1Checklist(selectedMenuUser, memberIndex >= 0 ? memberIndex : -1)}
                    className="w-full mt-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <FaEye className="w-3 h-3" /> Preview / Edit Step 1 Checklist
                  </button>
                )}
                {step.step === 1 && step.status === 'submitted' && (
                  <button
                    type="button"
                    onClick={() => handleUpdateTaxFilingStepStatus(1, 'in-progress', memberIndex)}
                    disabled={isUpdating}
                    className="w-full mt-2 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <FaUnlock className="w-3 h-3" /> Re-open Step 1
                  </button>
                )}
                {step.step === 3 && step3Status === 'submitted' && (
                  <p className="mt-2 text-[10px] text-green-600 dark:text-green-400">Step 3 completed by client.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTaxSummaryTable = (memberIndex = -1) => {
    const isPrimary = memberIndex < 0;
    const table = isPrimary
      ? getPrimarySummaryTable()
      : getMemberSummaryTable(selectedMenuUser?.members?.[memberIndex]);
    const title = isPrimary ? 'Primary Member Tax Filing Table' : 'Tax Filing Summary Table';

    const onTableChange = isPrimary
      ? handlePrimaryTableChange
      : (updater) => handleMemberTableChange(memberIndex, updater);

    const getTable = () => (isPrimary ? getPrimarySummaryTable() : getMemberSummaryTable(selectedMenuUser?.members?.[memberIndex]));

    return (
      <div>
        <h5 className="font-bold text-gray-900 dark:text-white mb-3">{title}</h5>
        {isPrimary && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Configure a custom summary table for the primary member&apos;s Tax Filing. This table will be visible to the user only after all four Tax Filing steps are completed.
          </p>
        )}
        <div className="border border-gray-200 dark:border-neutral-600 rounded-xl overflow-hidden">
          <div className="p-3 bg-gray-50 dark:bg-neutral-700 flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onTableChange((t) => {
                const columns = [...(t.columns || [])];
                const rows = (t.rows || []).map((r) => [...r]);
                columns.push(`Column ${columns.length + 1}`);
                return { ...t, columns, rows: rows.map((r) => [...r, '']) };
              })}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              + Add Column
            </button>
            <button
              type="button"
              onClick={() => onTableChange((t) => {
                const columns = t.columns || [];
                const rows = [...(t.rows || [])];
                rows.push(columns.map(() => ''));
                return { ...t, rows };
              })}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700"
            >
              + Add Row
            </button>
            <button
              type="button"
              onClick={() => onTableChange(() => cloneDefaultTable())}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40"
            >
              Reset Table
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-t border-gray-200 dark:border-neutral-600 text-xs">
              <thead className="bg-gray-50 dark:bg-neutral-700">
                <tr>
                  {(table.columns || []).map((col, colIndex) => (
                    <th key={colIndex} className="px-2 py-2 border-b border-gray-200 dark:border-neutral-600">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={col}
                          onChange={(e) => onTableChange((t) => {
                            const columns = [...(t.columns || [])];
                            columns[colIndex] = e.target.value;
                            return { ...t, columns };
                          })}
                          className="w-full px-2 py-1 rounded border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white font-semibold"
                          placeholder={`Column ${colIndex + 1}`}
                        />
                        {(table.columns?.length || 0) > 1 && (
                          <button
                            type="button"
                            onClick={() => onTableChange((t) => ({
                              ...t,
                              columns: (t.columns || []).filter((_, i) => i !== colIndex),
                              rows: (t.rows || []).map((row) => row.filter((_, i) => i !== colIndex)),
                            }))}
                            className="text-red-600 hover:text-red-800"
                            aria-label="Delete Column"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  {(table.columns?.length || 0) > 0 && (
                    <th className="px-2 py-2 border-b border-gray-200 dark:border-neutral-600 text-right text-gray-500 dark:text-gray-400 text-[10px]">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(table.rows || []).map((row, rowIndex) => (
                  <tr key={rowIndex} className="odd:bg-white even:bg-gray-50 dark:odd:bg-neutral-800 dark:even:bg-neutral-700/70">
                    {(table.columns || []).map((_, colIndex) => (
                      <td key={colIndex} className="px-2 py-1.5 border-b border-gray-100 dark:border-neutral-700">
                        <input
                          type="text"
                          value={row[colIndex] ?? ''}
                          onChange={(e) => onTableChange((t) => {
                            const rows = (t.rows || []).map((r) => [...r]);
                            if (!rows[rowIndex]) rows[rowIndex] = (t.columns || []).map(() => '');
                            rows[rowIndex][colIndex] = e.target.value;
                            return { ...t, rows };
                          })}
                          className="w-full px-2 py-1 rounded border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-900 dark:text-white"
                        />
                      </td>
                    ))}
                    {table.columns?.length > 0 && (
                      <td className="px-2 py-1.5 border-b border-gray-100 dark:border-neutral-700 text-right">
                        <button
                          type="button"
                          onClick={() => onTableChange((t) => ({
                            ...t,
                            rows: (t.rows || []).filter((_, idx) => idx !== rowIndex),
                          }))}
                          className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        >
                          Delete Row
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {handleUpdateTaxSummaryTable && (
            <div className="p-3 border-t border-gray-200 dark:border-neutral-600 flex justify-end">
              <button
                type="button"
                onClick={() => handleUpdateTaxSummaryTable(
                  selectedMenuUser._id,
                  isPrimary ? null : memberIndex,
                  getTable()
                )}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              >
                Save Table
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTaxFilingHistory = (memberIndex = null) => {
    const historyList = memberIndex === null
      ? selectedMenuUser?.taxFilingHistory
      : selectedMenuUser?.members?.[memberIndex]?.taxFilingHistory;
    if (!historyList?.length) return null;

    const historyType = memberIndex === null ? 'primary' : 'member';

    return (
      <div>
        <h5 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <FaHistory className="text-gray-500" /> Tax Filing History ({historyList.length})
        </h5>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {historyList.map((history, idx) => {
            const isEditing = editingHistory?.type === historyType
              && editingHistory?.historyIndex === idx
              && (historyType === 'primary' || editingHistory?.memberIndex === memberIndex);

            return (
              <div key={idx} className="p-3 bg-gray-50 dark:bg-neutral-700 rounded-lg text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">Form {history.assignedForm}</span>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            handleHistoryTaxDateUpdate(
                              selectedMenuUser._id,
                              memberIndex,
                              idx,
                              historyEditTaxYearEnd,
                              historyEditDueDate
                            );
                            setEditingHistory(null);
                          }}
                          className="text-green-600 hover:text-green-700 text-xs font-bold"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingHistory(null)}
                          className="text-gray-400 hover:text-gray-600 text-xs"
                        >
                          Cancel
                        </button>
                      </>
                    ) : handleHistoryTaxDateUpdate && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingHistory(
                            historyType === 'primary'
                              ? { type: 'primary', historyIndex: idx }
                              : { type: 'member', memberIndex, historyIndex: idx }
                          );
                          setHistoryEditTaxYearEnd(formatDateForInput(history.taxYearEndDate));
                          setHistoryEditDueDate(formatDateForInput(history.dueDate));
                        }}
                        className="text-primary-600 hover:text-primary-700 text-xs flex items-center gap-1"
                      >
                        <FaEdit /> Edit
                      </button>
                    )}
                    <span className="text-green-600 dark:text-green-400 text-xs">✓ Completed</span>
                  </div>
                </div>
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className="block text-[10px] text-gray-500">Tax Year End</label>
                      <input
                        type="date"
                        value={historyEditTaxYearEnd}
                        onChange={(e) => setHistoryEditTaxYearEnd(e.target.value)}
                        className="w-full px-2 py-1 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500">Due Date</label>
                      <input
                        type="date"
                        value={historyEditDueDate}
                        onChange={(e) => setHistoryEditDueDate(e.target.value)}
                        className="w-full px-2 py-1 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded text-xs"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Tax Year: {formatDateForDisplay(history.taxYearEndDate)} | Due: {formatDateForDisplay(history.dueDate)} | Completed: {formatDateForDisplay(history.completedAt)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTaxFilingActivityReport = (memberIndex = -1) => {
    const isPrimary = memberIndex < 0;
    const subject = isPrimary ? selectedMenuUser : selectedMenuUser?.members?.[memberIndex];
    if (!subject) return null;

    const stepsList = getTaxFilingStepsList(memberIndex);
    const stepCompletionCount = [1, 2, 3, 4].filter((s) => subject[`step${s}CompletionDate`]).length;
    const label = isPrimary ? 'Primary Member' : (subject.name || `Member ${memberIndex + 1}`);

    return (
      <div className="mt-8 pt-8 border-t border-gray-200 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaHistory className="text-primary-500" /> Tax Filing Activity Report
        </h5>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
          <h6 className="font-bold text-lg text-blue-900 dark:text-blue-300 mb-4 flex items-center gap-2 flex-wrap">
            <FaClipboardCheck /> {label}
            {subject.assignedForm && (
              <span className="px-2.5 py-0.5 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-semibold">
                Form {subject.assignedForm}
              </span>
            )}
          </h6>
          <div className="space-y-4">
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm">
              <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tax Filing Enabled Date</h6>
              {formatDetailedDateTime(subject.taxFilingEnabledAt)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {stepsList.map((step) => (
                <div key={step.step} className="bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Step {step.step}: {step.title}
                    </h6>
                    {subject.assignedForm && (
                      <span className="shrink-0 px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 text-[10px] font-bold">
                        {subject.assignedForm}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] font-bold uppercase text-primary-600 mb-2">
                    {formatTaxFilingStatusShort(step.status)}
                  </p>
                  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Completed At</p>
                  {formatDetailedDateTime(subject[`step${step.step}CompletionDate`])}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-5 border border-purple-200 dark:border-purple-800">
          <h6 className="font-bold text-lg text-purple-900 dark:text-purple-300 mb-4">Activity Summary</h6>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-primary-600">
                {isPrimary ? (selectedMenuUser?.isTaxFilingEnabled ? '✓' : '✗') : (subject.assignedForm ? '✓' : '✗')}
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Service Active</div>
            </div>
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{stepCompletionCount}</div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Steps Completed</div>
            </div>
            {isPrimary ? (
              <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{selectedMenuUser?.members?.length || 0}</div>
                <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Total Members</div>
              </div>
            ) : (
              <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-blue-600 uppercase">{subject.paymentStatus || '—'}</div>
                <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Payment Status</div>
              </div>
            )}
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-orange-600">
                {(isPrimary ? selectedMenuUser?.taxFilingLocked : subject.taxFilingLocked) ? 'Locked' : 'Active'}
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Filing Status</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPrimaryMember = () => (
    <>
      <div className="grid gap-4 lg:grid-cols-2 items-start">
        <div className={formCardClass}>
          <label className={formLabelClass}>Assign Form</label>
          <div className="flex gap-2">
            <select
              className={`${formSelectClass} flex-1`}
              value={selectedMenuUser?.assignedForm || '1040'}
              onChange={(e) => setSelectedMenuUser({ ...selectedMenuUser, assignedForm: e.target.value })}
            >
              <option value="1040">1040</option>
              <option value="1065">1065</option>
              <option value="1120-S">1120-S</option>
              <option value="1120">1120</option>
            </select>
            <button
              type="button"
              className="btn-premium px-4 py-2.5 text-sm shrink-0"
              onClick={() => handleFormAssignment(selectedMenuUser._id, selectedMenuUser.assignedForm || '1040')}
            >
              Assign
            </button>
          </div>
        </div>
        <div className={formCardClass}>
          <label className={formLabelClass}>Payment Status</label>
          <select
            className={formSelectClass}
            value={selectedMenuUser?.paymentStatus || (selectedMenuUser?.taxFilingLocked ? 'unpaid' : 'paid')}
            onChange={(e) => handlePaymentStatusUpdate(selectedMenuUser._id, -1, e.target.value)}
          >
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="wip-unpaid">WIP–Unpaid</option>
            <option value="p-extension">P Extension</option>
            <option value="up-extension">UP Extension</option>
          </select>
        </div>
      </div>

      <div className={formCardClass}>
        <label className={formLabelClass}>Tax Year End Date & Due Date</label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <input type="date" value={primaryTaxYearEndDate || formatDateForInput(selectedMenuUser?.taxYearEndDate)} onChange={(e) => setPrimaryTaxYearEndDate(e.target.value)} className={formInputClass} />
          <input type="date" value={primaryDueDate || formatDateForInput(selectedMenuUser?.dueDate)} onChange={(e) => setPrimaryDueDate(e.target.value)} className={formInputClass} />
        </div>
        <button
          type="button"
          onClick={() => {
            const tyed = primaryTaxYearEndDate || formatDateForInput(selectedMenuUser?.taxYearEndDate);
            const dd = primaryDueDate || formatDateForInput(selectedMenuUser?.dueDate);
            if (tyed) {
              handleTaxDateUpdate(selectedMenuUser._id, tyed, dd);
              setPrimaryTaxYearEndDate('');
              setPrimaryDueDate('');
            }
          }}
          disabled={!primaryTaxYearEndDate && !selectedMenuUser?.taxYearEndDate}
          className="w-full px-4 py-2.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 text-sm"
        >
          Update Tax Dates
        </button>
        {selectedMenuUser?.taxYearEndDate && selectedMenuUser?.dueDate && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-xl text-xs">
            <div className="flex items-center justify-between gap-2">
              <div>
                <strong>Tax Year End:</strong> {formatDateForDisplay(selectedMenuUser.taxYearEndDate)}<br />
                <strong>Due Date:</strong> {formatDateForDisplay(selectedMenuUser.dueDate)}
                {getDueDatePeriod(selectedMenuUser.assignedForm) && (
                  <span className="text-primary-600"> ({getDueDatePeriod(selectedMenuUser.assignedForm)})</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleSendTaxReminder(selectedMenuUser._id)}
                disabled={manualReminderLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-[10px] font-bold rounded-lg shrink-0"
              >
                {manualReminderLoading ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaBell />}
                Instant Reminder
              </button>
            </div>
            {renderReminderTracking(selectedMenuUser.oneMonthReminderSent, selectedMenuUser.fifteenDayReminderSent)}
          </div>
        )}
      </div>

      {renderTaxFilingStepsStatus(-1)}

      {renderTaxSummaryTable(-1)}

      <div>
        <h5 className="font-bold text-gray-900 dark:text-white mb-3">Tax Filing Lock Status</h5>
        {renderLockStatus(selectedMenuUser?.taxFilingLocked, null)}
      </div>

      {renderMoveToHistory(null)}

      {renderTaxFilingHistory(null)}

      {renderTaxFilingActivityReport(-1)}

      <div>
        <h5 className="font-bold text-gray-900 dark:text-white mb-3">User Feedback</h5>
        {renderFeedback(selectedMenuUser?.feedback)}
      </div>
    </>
  );

  const renderMemberTab = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 dark:bg-neutral-800/50 p-4 rounded-2xl border border-gray-100 dark:border-neutral-700">
        <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Add New Member</h5>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter new member name..."
            value={newMemberName}
            onChange={(e) => setNewMemberName(e.target.value)}
            className={`${formInputClass} flex-1`}
          />
          <button
            type="button"
            onClick={handleAddMemberWithService}
            disabled={selectedMenuUser?.members?.length >= 50}
            className="px-6 py-2.5 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            <FaPlus /> Add Member
          </button>
        </div>
      </div>

      {selectedMenuUser?.members?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedMenuUser.members.map((member, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveMemberIndex(index)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 border ${
                activeMemberIndex === index
                  ? 'bg-primary-600 border-primary-600 text-white shadow-md'
                  : 'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-400 hover:border-primary-500'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${member?.paymentStatus === 'paid' ? 'bg-green-500' : 'bg-orange-500'}`} />
              {member.name || `Member ${index + 1}`}
            </button>
          ))}
        </div>
      )}

      {selectedMenuUser?.members?.[activeMemberIndex] ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden">
          <div className="p-4 bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 font-bold text-lg">
                {(selectedMenuUser.members[activeMemberIndex].name || '?')[0].toUpperCase()}
              </div>
              <input
                type="text"
                value={selectedMenuUser.members[activeMemberIndex].name || ''}
                placeholder="Member Name"
                onChange={(e) => {
                  const u = [...selectedMenuUser.members];
                  u[activeMemberIndex].name = e.target.value;
                  setSelectedMenuUser({ ...selectedMenuUser, members: u });
                }}
                className="bg-transparent border-none text-gray-900 dark:text-white font-bold text-base focus:ring-0 p-0 w-48"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleMemberNameUpdate(selectedMenuUser._id, activeMemberIndex, selectedMenuUser.members[activeMemberIndex].name)}
                className="px-3 py-1.5 bg-primary-600 text-white text-xs font-bold rounded-lg"
              >
                Update Name
              </button>
              <button
                type="button"
                onClick={() => removeMember(selectedMenuUser._id, activeMemberIndex)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                title="Delete Member"
              >
                <FaTrash size={14} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className={formCardClass}>
                <label className={formLabelClass}>Assigned Form</label>
                <div className="flex gap-2">
                  <select
                    className={`${formSelectClass} flex-1`}
                    value={selectedMenuUser.members[activeMemberIndex].assignedForm || '1040'}
                    onChange={(e) => {
                      const u = [...selectedMenuUser.members];
                      u[activeMemberIndex].assignedForm = e.target.value;
                      setSelectedMenuUser({ ...selectedMenuUser, members: u });
                    }}
                  >
                    <option value="1040">1040</option>
                    <option value="1065">1065</option>
                    <option value="1120-S">1120-S</option>
                    <option value="1120">1120</option>
                  </select>
                  <button
                    type="button"
                    className="btn-premium px-4 py-2.5 text-sm shrink-0"
                    onClick={() => handleMemberFormAssignment(
                      selectedMenuUser._id,
                      activeMemberIndex,
                      selectedMenuUser.members[activeMemberIndex].assignedForm || '1040'
                    )}
                  >
                    Assign
                  </button>
                </div>
              </div>
              <div className={formCardClass}>
                <label className={formLabelClass}>Payment Status</label>
                <select
                  className={formSelectClass}
                  value={selectedMenuUser.members[activeMemberIndex]?.paymentStatus || (selectedMenuUser.members[activeMemberIndex]?.taxFilingLocked ? 'unpaid' : 'paid')}
                  onChange={(e) => handlePaymentStatusUpdate(selectedMenuUser._id, activeMemberIndex, e.target.value)}
                >
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="wip-unpaid">WIP–Unpaid</option>
                  <option value="p-extension">P Extension</option>
                  <option value="up-extension">UP Extension</option>
                </select>
              </div>
            </div>

            <div className={formCardClass}>
              <label className={formLabelClass}>Tax Year End & Due Date</label>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="date"
                  value={memberTaxYearEndDates[activeMemberIndex] || formatDateForInput(selectedMenuUser.members[activeMemberIndex]?.taxYearEndDate)}
                  onChange={(e) => setMemberTaxYearEndDates({ ...memberTaxYearEndDates, [activeMemberIndex]: e.target.value })}
                  className={formInputClass}
                />
                <input
                  type="date"
                  value={memberDueDates[activeMemberIndex] || formatDateForInput(selectedMenuUser.members[activeMemberIndex]?.dueDate)}
                  onChange={(e) => setMemberDueDates({ ...memberDueDates, [activeMemberIndex]: e.target.value })}
                  className={formInputClass}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const tyed = memberTaxYearEndDates[activeMemberIndex] || formatDateForInput(selectedMenuUser.members[activeMemberIndex]?.taxYearEndDate);
                  const dd = memberDueDates[activeMemberIndex] || formatDateForInput(selectedMenuUser.members[activeMemberIndex]?.dueDate);
                  if (tyed) {
                    handleMemberTaxDateUpdate(selectedMenuUser._id, activeMemberIndex, tyed, dd);
                    setMemberTaxYearEndDates({ ...memberTaxYearEndDates, [activeMemberIndex]: '' });
                    setMemberDueDates({ ...memberDueDates, [activeMemberIndex]: '' });
                  }
                }}
                disabled={!memberTaxYearEndDates[activeMemberIndex] && !selectedMenuUser.members[activeMemberIndex]?.taxYearEndDate}
                className="w-full px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50"
              >
                Update Dates
              </button>
              {selectedMenuUser.members[activeMemberIndex]?.taxYearEndDate && (
                <div className="mt-3 text-[10px] p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span>
                      <strong>Current:</strong> {formatDateForDisplay(selectedMenuUser.members[activeMemberIndex].taxYearEndDate)}
                      {selectedMenuUser.members[activeMemberIndex].dueDate && ` to ${formatDateForDisplay(selectedMenuUser.members[activeMemberIndex].dueDate)}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleSendTaxReminder(selectedMenuUser._id, activeMemberIndex)}
                      disabled={manualReminderLoading}
                      className="flex items-center gap-1 px-2 py-1 bg-primary-600 text-white text-[9px] font-bold rounded-md"
                    >
                      <FaBell className="w-2 h-2" /> Reminder
                    </button>
                  </div>
                  {renderReminderTracking(
                    selectedMenuUser.members[activeMemberIndex].oneMonthReminderSent,
                    selectedMenuUser.members[activeMemberIndex].fifteenDayReminderSent
                  )}
                </div>
              )}
            </div>

            {renderTaxFilingStepsStatus(activeMemberIndex)}

            <div>
              <h5 className="font-bold text-gray-900 dark:text-white mb-3">Member Feedback</h5>
              {renderFeedback(selectedMenuUser.members[activeMemberIndex]?.feedback)}
            </div>

            {renderTaxSummaryTable(activeMemberIndex)}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderMoveToHistory(activeMemberIndex)}
              {selectedMenuUser.members[activeMemberIndex]?.taxFilingLocked && (
                <div>
                  <h5 className="font-bold text-gray-900 dark:text-white mb-3">Tax Filing Lock Status</h5>
                  {renderLockStatus(true, activeMemberIndex)}
                </div>
              )}
            </div>

            {renderTaxFilingHistory(activeMemberIndex)}

            {renderTaxFilingActivityReport(activeMemberIndex)}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-8">No members added yet. Use the form above to add a member.</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex border-b border-gray-200 dark:border-neutral-700">
        <button
          type="button"
          onClick={() => setTaxFilingSubTab('primary-member')}
          className={`px-5 py-2.5 text-sm font-semibold transition-all ${
            taxFilingSubTab === 'primary-member'
              ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50 dark:bg-primary-900/10'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Primary Member
        </button>
        <button
          type="button"
          onClick={() => setTaxFilingSubTab('member')}
          className={`px-5 py-2.5 text-sm font-semibold transition-all ${
            taxFilingSubTab === 'member'
              ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50 dark:bg-primary-900/10'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Member {selectedMenuUser?.members?.length > 0 ? `(${selectedMenuUser.members.length})` : ''}
        </button>
      </div>

      {taxFilingSubTab === 'primary-member' ? renderPrimaryMember() : renderMemberTab()}
    </div>
  );
};

export default TaxFilingTab;
